import { UserAvatar } from "@promptlayer/ui";
import { UserIcon } from "lucide-react";
import { useParams } from "react-router-dom";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/auth-context";
import { useWorkspaceMembers } from "@/queries";
import { toNormalCase } from "@/utils/strings";

interface UserFilterProps {
  value: number | null;
  onChange: (userId: number | null) => void;
}

const UserFilter: React.FC<UserFilterProps> = ({ value, onChange }) => {
  const { workspaceId } = useParams();
  const auth = useAuth();
  const userToken = auth?.userToken ?? "";
  const workspaceIdNum = workspaceId ? parseInt(workspaceId, 10) : null;

  const { data: membersData, isLoading } = useWorkspaceMembers(userToken, workspaceIdNum);

  const members = membersData?.workspace_members ?? [];

  const handleUserSelect = (userId: string) => {
    if (userId === "clear") {
      onChange(null);
      return;
    }

    onChange(parseInt(userId, 10));
  };

  const getDisplayValue = () => {
    if (!value) {
      return "User";
    }

    const member = members.find(m => m.user_id === value);
    return member?.name || member?.email || "Unknown User";
  };

  const hasActiveFilter = value !== null && value !== undefined;

  if (isLoading || members.length === 0) {
    return null;
  }

  return (
    <Select value={value?.toString()} onValueChange={handleUserSelect}>
      <SelectTrigger
        className={`h-8 w-auto min-w-[100px] gap-1.5 border-0 bg-transparent px-2 py-1 text-sm font-medium shadow-none transition-colors hover:bg-gray-100 focus:bg-gray-100 focus:ring-0 ${
          hasActiveFilter ? "bg-gray-100 text-gray-900" : "text-gray-600 hover:text-gray-900"
        }`}
        aria-label={`Filter by user${hasActiveFilter ? " (active)" : ""}`}
      >
        <UserIcon className="h-4 w-4" />
        <SelectValue placeholder="User">{getDisplayValue()}</SelectValue>
      </SelectTrigger>
      <SelectContent align="start" className="max-h-80 w-64">
        {members.map(member => (
          <SelectItem
            key={member.user_id}
            value={member.user_id?.toString() ?? "unknown"}
            className="text-sm"
          >
            <div className="flex items-center gap-2">
              <UserAvatar email={member.email ?? ""} />
              <div className="flex flex-col">
                <span>
                  {member.name || toNormalCase(member.email?.split("@")[0] ?? "") || "Unknown"}
                </span>
                {member.email && <span className="text-xs text-gray-500">{member.email}</span>}
              </div>
            </div>
          </SelectItem>
        ))}
        {hasActiveFilter && (
          <>
            <div className="my-1 h-px bg-gray-200" />
            <SelectItem value="clear" className="text-sm text-gray-500">
              Clear filter
            </SelectItem>
          </>
        )}
      </SelectContent>
    </Select>
  );
};

export default UserFilter;
